import React, { ReactNode } from 'react';

import differenceInMinutes from 'date-fns/differenceInMinutes';
import { camelCase, capitalize, isNil } from 'lodash';
import { IntlShape } from 'react-intl';

import { ConvertToCamelCase, ExcludeNil, Mutable } from '@rbi-ctg/frontend';
import { IServerOrder } from '@rbi-ctg/menu';
import { DiscountTypes } from 'enums/menu';
import {
  IOffersFragment,
  PaymentMethod,
  PromotionType,
  ServiceMode,
} from 'generated/graphql-gateway';
import { TLocalizationKey } from 'types/i18n';
import { CartEntryType } from 'utils/cart';

import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from '../types';

import { makeIncentiveOutOfDayPartMessage } from './incentives-availability';

type TDayOfTheWeekKey = Extract<
  TLocalizationKey,
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
>;

type TPaymentMethod = `${PaymentMethod}`;

const serviceModeLocalizedKeysMap: Record<ServiceMode, TLocalizationKey> = {
  [ServiceMode.DELIVERY]: 'delivery',
  [ServiceMode.CATERING_DELIVERY]: 'cateringDelivery',
  [ServiceMode.CATERING_PICKUP]: 'cateringPickUp',
  [ServiceMode.DRIVE_THRU]: 'driveThru',
  [ServiceMode.CURBSIDE]: 'curbside',
  [ServiceMode.EAT_IN]: 'eatIn',
  [ServiceMode.TABLE_SERVICE]: 'tableService',
  [ServiceMode.TAKEOUT]: 'pickUp',
};

export type OrderSubtotalAndTotalCents = {
  orderSubtotalCents: number;
  orderTotalCents: number;
};

interface IEvaluationResultMessageParams {
  evaluationResult: IIncentiveEvaluationResult;
  formatters: IntlShape;
  incentiveType: PromotionType;
}

export const getEvaluationResultMessage = ({
  evaluationResult,
  formatters,
  incentiveType: type,
}: IEvaluationResultMessageParams): string | ReactNode => {
  const { formatDate, formatMessage, formatTime } = formatters;
  const { code, currentValue, targetValue } = evaluationResult;
  const incentiveType = type.toLowerCase();
  let message: string | ReactNode = formatMessage(
    { id: 'incentiveFeedbackDefault' },
    { incentiveType }
  );

  switch (code) {
    case IncentiveEvaluationErrorCodes.BELOW_MINIMUM_SPEND: {
      if (isNil(currentValue) || isNil(targetValue)) {
        break;
      }

      message = formatMessage(
        { id: 'incentiveFeedbackSubtotal' },
        {
          incentiveType,
          currentValue: `${currentValue / 100}`,
          targetValue: `${targetValue / 100}`,
          calculatedValue: `${(targetValue - currentValue) / 100}`,
        }
      );

      break;
    }

    case IncentiveEvaluationErrorCodes.BELOW_MINIMUM_TOTAL_SPEND: {
      if (isNil(currentValue) || isNil(targetValue)) {
        break;
      }

      message = formatMessage(
        { id: 'incentiveFeedbackTotalSpend' },
        {
          incentiveType,
          currentValue: `${currentValue / 100}`,
          targetValue: `${targetValue / 100}`,
          calculatedValue: `${(targetValue - currentValue) / 100}`,
        }
      );

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_DAY_OF_THE_WEEK: {
      if (!targetValue?.length) {
        break;
      }
      message = formatMessage(
        {
          id: 'incentiveFeedbackInvalidDay',
        },
        {
          incentiveType,
          targetValue:
            targetValue.length > 1 ? (
              <ul>
                {targetValue.map((id: TDayOfTheWeekKey) => (
                  <li key={id}>{`${formatMessage({ id })}`}</li>
                ))}
              </ul>
            ) : (
              formatMessage({ id: targetValue[0] })
            ),
        }
      );
      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_TIME_SPAN: {
      const { endTime, startTime } = targetValue;
      if (!startTime || !endTime) {
        break;
      }

      message = formatMessage(
        {
          id: 'incentiveFeedbackInvalidTime',
        },
        {
          incentiveType,
          startTime: formatTime(timeToDate(startTime)),
          endTime: formatTime(timeToDate(endTime)),
        }
      );
      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_END_DATE: {
      const { endDate } = evaluationResult.targetValue;
      if (!endDate) {
        break;
      }

      message = formatMessage(
        {
          id: 'incentiveFeedbackExpired',
        },
        {
          incentiveType,
          endDate: formatDate(endDate, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            calendar: 'gregory',
          }),
        }
      );

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE: {
      const serviceModes = targetValue;
      if (!serviceModes?.length) {
        break;
      }

      const messageKey = serviceModes.some((sm: ServiceMode) => sm === ServiceMode.DELIVERY)
        ? ServiceMode.DELIVERY
        : ServiceMode.TAKEOUT;

      message = formatMessage(
        {
          id: 'incentiveFeedbackServiceMode',
        },
        {
          incentiveType,
          targetValue: formatMessage({ id: serviceModeLocalizedKeysMap[messageKey] }),
        }
      );

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD: {
      const paymentMethod = targetValue as TPaymentMethod;
      if (!paymentMethod) {
        break;
      }
      const id = camelCase(paymentMethod) as ConvertToCamelCase<TPaymentMethod>;
      const formattedMessage = formatMessage({ id });

      message = formatMessage(
        {
          id: 'incentiveFeedbackPaymentMethod',
        },
        {
          incentiveType,
          targetValue: capitalize(formattedMessage),
        }
      );

      break;
    }

    case IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION: {
      message = formatMessage({ id: 'incentiveFeedbackMaxRedemptions' }, { incentiveType });
      break;
    }

    case IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS: {
      message = formatMessage({ id: 'incentiveFeedbackCartRequirement' }, { incentiveType });
      break;
    }

    case IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS_EXCLUSION: {
      message = formatMessage({ id: 'incentiveFeedbackCartRequirementExclusion' });
      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_STORE_ID: {
      message = formatMessage({ id: 'incentiveFeedbackInvalidStore' }, { incentiveType });
      break;
    }

    case IncentiveEvaluationErrorCodes.OUT_OF_DAY_PART: {
      const { incentive, dayParts } = targetValue;
      message = makeIncentiveOutOfDayPartMessage({ incentive, dayParts, formatters });
      break;
    }

    case IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE: {
      message = formatMessage({ id: 'incentiveFeedbackNotAvailableInStore' });
      break;
    }

    case IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE: {
      message = formatMessage({ id: 'incentiveFeedbackOfferNotAvailable' });
      break;
    }

    case IncentiveEvaluationErrorCodes.WITHIN_COOL_DOWN_PERIOD: {
      const calculatedValue = differenceInMinutes(new Date(targetValue), new Date(currentValue));

      if (!isNaN(calculatedValue)) {
        message = formatMessage(
          { id: 'incentiveFeedbackWithinCooldown' },
          { calculatedValue, incentiveType }
        );
      }
      break;
    }

    case IncentiveEvaluationErrorCodes.AUTHENTICATION_REQUIRED: {
      message = formatMessage({ id: 'incentiveFeedbackAuthenticationRequired' }, { incentiveType });
      break;
    }

    // All errors will be transformed into messages here
    default: {
      return message;
    }
  }

  return message;
};

interface IFeedbackMaps {
  offersMapById: Record<string, string>;
  offerFeedbackMap: IncentiveEvaluationMap;
}

export const createFeedbackMaps = (offers: readonly IOffersFragment[]): IFeedbackMaps => {
  const { offerFeedbackMap, offersMapById } = offers.reduce<IFeedbackMaps>(
    (acc, offer) => {
      const offerId = offer.id;

      acc.offersMapById[offerId] = offerId;
      if (offer?.errors?.length) {
        acc.offerFeedbackMap[offerId] = offer.errors as Mutable<
          ExcludeNil<IOffersFragment['errors']>
        >;
      }

      return acc;
    },
    { offerFeedbackMap: {}, offersMapById: {} }
  );

  return { offerFeedbackMap, offersMapById };
};

const timeToDate = (time: string): Date => {
  const today = new Date();

  const [hr, min, sec] = time.split(':');
  today.setHours(+hr);
  today.setMinutes(+min);
  today.setSeconds(+sec);

  return today;
};

export const getServerOrderSubtotalAndTotalBeforeOfferDiscounts = (
  serverOrder: IServerOrder | null,
  isCartTotalNegative: boolean = false
): OrderSubtotalAndTotalCents => {
  if (isCartTotalNegative || !serverOrder?.cart) {
    return {
      orderSubtotalCents: 0,
      orderTotalCents: 0,
    };
  }

  const hasOfferDiscountApplied = serverOrder.cart.cartEntries?.some(
    entry => entry.type === CartEntryType.offerDiscount
  );
  const orderDiscounts = serverOrder.cart?.discounts ?? [];

  const subtotalCents = serverOrder.cart?.subTotalCents ?? 0;
  const totalCents = serverOrder.cart?.totalCents ?? 0;

  const offersDiscountsCents = orderDiscounts.reduce(
    (acc, discount) =>
      discount.name === DiscountTypes.COMBO_AND_OFFER_DISCOUNTS ? acc + discount.value : acc,
    0
  );

  return {
    orderSubtotalCents: hasOfferDiscountApplied
      ? subtotalCents + offersDiscountsCents
      : subtotalCents,
    orderTotalCents: hasOfferDiscountApplied ? totalCents + offersDiscountsCents : totalCents,
  };
};
